import { useRef, useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '../lib/api';
import { useChannels } from '../lib/channels-context';
import { putWithProgress } from '../lib/upload';
import type { Video } from '../lib/types';
import { Thumb } from '../components/Thumb';

export function Upload(): JSX.Element {
  const [file, setFile] = useState<File | null>(null);
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const { channels } = useChannels();
  const [channelId, setChannelId] = useState<string>('');
  const [visibility, setVisibility] = useState<'company' | 'link'>('company');
  const [progress, setProgress] = useState(0);
  const [busy, setBusy] = useState(false);
  const [toast, setToast] = useState('');
  const [dragging, setDragging] = useState(false);
  const [uploads, setUploads] = useState<Video[]>([]);
  const fileInput = useRef<HTMLInputElement | null>(null);
  const nav = useNavigate();

  useEffect(() => {
    api.myUploads().then(setUploads).catch(() => setUploads([]));
  }, []);

  const pickFile = (f: File | undefined | null): void => {
    if (!f) return;
    if (!/^video\//.test(f.type) && !/\.(mp4|mov|m4v)$/i.test(f.name)) {
      flash('動画ファイル（MP4 / MOV）を選んでください');
      return;
    }
    setFile(f);
    if (!title) setTitle(f.name.replace(/\.[^.]+$/, ''));
  };

  useEffect(() => {
    if (channels.length > 0 && !channelId) setChannelId(channels[0].channelId);
  }, [channels, channelId]);

  const submit = async (): Promise<void> => {
    if (!file) return flash('動画ファイルを選んでください');
    if (!title) return flash('タイトルを入力してください');
    setBusy(true);
    try {
      const { videoId, uploadUrl } = await api.requestUpload({
        title,
        description,
        channelId,
        contentType: file.type || 'video/mp4',
        filename: file.name,
        visibility,
        tags: [],
      });
      if (uploadUrl) {
        await putWithProgress(uploadUrl, file, setProgress);
        await api.publishVideo(videoId);
      }
      flash('公開しました');
      setTimeout(() => nav('/'), 800);
    } catch (e) {
      flash(`失敗しました: ${(e as Error).message}`);
    } finally {
      setBusy(false);
    }
  };

  const flash = (m: string): void => {
    setToast(m);
    setTimeout(() => setToast(''), 2400);
  };

  return (
    <div style={{ padding: '26px 30px 60px', maxWidth: 760 }}>
      <h1 style={{ fontFamily: 'var(--font-heading)', fontWeight: 700, fontSize: 22, margin: '0 0 22px' }}>
        動画をアップロード
      </h1>

      <div
        onClick={() => fileInput.current?.click()}
        onDragEnter={(e) => {
          e.preventDefault();
          e.stopPropagation();
          setDragging(true);
        }}
        onDragOver={(e) => {
          e.preventDefault();
          e.stopPropagation();
          e.dataTransfer.dropEffect = 'copy';
          if (!dragging) setDragging(true);
        }}
        onDragLeave={(e) => {
          e.preventDefault();
          e.stopPropagation();
          setDragging(false);
        }}
        onDrop={(e) => {
          e.preventDefault();
          e.stopPropagation();
          setDragging(false);
          pickFile(e.dataTransfer.files?.[0]);
        }}
        style={{
          border: `1.5px dashed ${dragging ? 'var(--brand)' : 'var(--border-strong)'}`,
          borderRadius: 16,
          background: dragging ? 'var(--surface-tint)' : 'var(--surface)',
          padding: 40,
          textAlign: 'center',
          cursor: 'pointer',
          marginBottom: 24,
          transition: 'background .15s ease, border-color .15s ease',
        }}
      >
        <div style={{ fontSize: 15, fontWeight: 700, marginBottom: 7, fontFamily: 'var(--font-heading)' }}>
          {file
            ? `✓ ${file.name}（${humanBytes(file.size)}）`
            : dragging
              ? 'ここにドロップ'
              : 'ここに動画ファイルをドロップ、またはクリックして選択'}
        </div>
        <div style={{ fontSize: 12, color: 'var(--ink-quiet)' }}>MP4 / MOV · 最大 5GB</div>
        <input
          ref={fileInput}
          type="file"
          accept="video/mp4,video/quicktime"
          style={{ display: 'none' }}
          onChange={(e) => pickFile(e.target.files?.[0])}
        />
      </div>

      <div className="card" style={{ display: 'flex', flexDirection: 'column', gap: 18, padding: 22 }}>
        <label style={fieldLabel}>
          <span style={fieldTitle}>タイトル</span>
          <input className="input" value={title} onChange={(e) => setTitle(e.target.value)} />
        </label>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
          <label style={fieldLabel}>
            <span style={fieldTitle}>チャンネル</span>
            <select className="input" value={channelId} onChange={(e) => setChannelId(e.target.value)}>
              {channels.map((c) => (
                <option key={c.channelId} value={c.channelId}>
                  {c.name}
                </option>
              ))}
            </select>
          </label>
          <label style={fieldLabel}>
            <span style={fieldTitle}>公開範囲</span>
            <select className="input" value={visibility} onChange={(e) => setVisibility(e.target.value as 'company')}>
              <option value="company">全社員に公開</option>
              <option value="link">リンクを知っている人のみ</option>
            </select>
          </label>
        </div>
        <label style={fieldLabel}>
          <span style={fieldTitle}>説明</span>
          <textarea
            className="input"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            style={{ minHeight: 84, resize: 'vertical', lineHeight: 1.7 }}
          />
        </label>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, borderTop: '1px solid var(--border-soft)', paddingTop: 18 }}>
          {busy && (
            <span style={{ fontSize: 12, color: 'var(--ink-quiet)' }}>アップロード中 {progress}%</span>
          )}
          <button
            className="btn"
            onClick={submit}
            disabled={busy || !file || !title}
            style={{ marginLeft: 'auto', opacity: busy || !file || !title ? 0.6 : 1 }}
          >
            {busy ? '公開中…' : '公開する'}
          </button>
        </div>
      </div>

      <div style={{ marginTop: 36 }}>
        <div style={{ fontFamily: 'var(--font-heading)', fontWeight: 700, fontSize: 16, marginBottom: 12 }}>
          自分のアップロード
        </div>
        {uploads.length === 0 ? (
          <div style={{ padding: '18px 4px', color: 'var(--ink-quiet)', fontSize: 13 }}>
            まだアップロードした動画はありません。
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
            {uploads.map((v) => (
              <div
                key={v.videoId}
                onClick={() => nav(`/videos/${v.videoId}`)}
                style={{
                  display: 'flex',
                  gap: 14,
                  alignItems: 'center',
                  padding: '12px 14px',
                  background: 'var(--surface)',
                  border: '1px solid var(--border-soft)',
                  borderRadius: 13,
                  cursor: 'pointer',
                }}
              >
                <Thumb video={v} radius={9} showLabel={false} showDuration={false} style={{ width: 132, flex: 'none' }} />
                <div style={{ minWidth: 0, flex: 1 }}>
                  <div style={{ fontSize: 13.5, fontWeight: 700, marginBottom: 4 }}>{v.title}</div>
                  <div style={{ fontSize: 11.5, color: 'var(--ink-quiet)' }}>
                    {v.channelName} · {statusLabel(v.status)}
                  </div>
                </div>
                <span style={{ fontSize: 12, color: 'var(--brand)', fontWeight: 700, flex: 'none' }}>開く →</span>
              </div>
            ))}
          </div>
        )}
      </div>

      {toast && (
        <div
          style={{
            position: 'fixed',
            bottom: 26,
            left: '50%',
            transform: 'translateX(-50%)',
            background: 'var(--ink)',
            color: '#fff',
            fontSize: 13,
            padding: '13px 22px',
            borderRadius: 999,
            boxShadow: '0 8px 24px rgba(31,42,68,0.16)',
            zIndex: 20,
          }}
        >
          {toast}
        </div>
      )}
    </div>
  );
}

function statusLabel(status: Video['status']): string {
  switch (status) {
    case 'published':
      return '公開中';
    case 'processing':
      return '処理中';
    case 'archived':
      return 'アーカイブ';
    default:
      return '';
  }
}

function humanBytes(n: number): string {
  if (n < 1024) return `${n}B`;
  if (n < 1024 ** 2) return `${(n / 1024).toFixed(1)}KB`;
  if (n < 1024 ** 3) return `${(n / 1024 ** 2).toFixed(1)}MB`;
  return `${(n / 1024 ** 3).toFixed(1)}GB`;
}

const fieldLabel: React.CSSProperties = { display: 'flex', flexDirection: 'column', gap: 7 };
const fieldTitle: React.CSSProperties = { fontSize: 12.5, fontWeight: 700 };
